// The host's edit of one card: which picture, how it is cropped and adjusted, and what is drawn
// on it. Every position and size is a fraction of the canvas so the same edit renders at any
// pixel size (the editor's stage, the 1080-wide export, a thumbnail).
import { z } from 'zod';

export const CANVASES = ['4:5', '1:1', '1.91:1', '9:16'] as const;
export type Canvas = (typeof CANVASES)[number];

/** Delivery size per canvas: what the flattened JPEG is exported at. */
export const CANVAS_SIZE: Record<Canvas, { width: number; height: number }> = {
  '4:5': { width: 1080, height: 1350 },
  '1:1': { width: 1080, height: 1080 },
  '1.91:1': { width: 1080, height: 566 },
  '9:16': { width: 1080, height: 1920 },
};

const fraction = z.number().min(0).max(1);
const signed = z.number().min(-1).max(1);
const color = z.string().regex(/^#[0-9a-f]{6}$/i);

export const textLayerSchema = z.object({
  id: z.string().min(1).max(40),
  kind: z.literal('text'),
  text: z.string().max(300),
  x: fraction,
  y: fraction,
  w: fraction,
  rotate: z.number().min(-180).max(180).default(0),
  /** Font size as a fraction of the canvas width. */
  size: z.number().min(0.01).max(0.5),
  font: z.enum(['sans', 'serif']).default('sans'),
  /** Bold reads on moving footage; regular is the quieter picture-card default. */
  weight: z.enum(['regular', 'bold']).optional(),
  color: color.default('#ffffff'),
  align: z.enum(['left', 'center', 'right']).default('center'),
  /** Background pill colour behind the text, or none. */
  pill: color.nullable().default(null),
  /** A soft dark gradient under and around the words, so they read on a bright photo. */
  shade: z.boolean().default(false),
});

export const emojiLayerSchema = z.object({
  id: z.string().min(1).max(40),
  kind: z.literal('emoji'),
  char: z.string().min(1).max(16),
  x: fraction,
  y: fraction,
  size: z.number().min(0.02).max(0.8),
  rotate: z.number().min(-180).max(180).default(0),
});

export const drawLayerSchema = z.object({
  id: z.string().min(1).max(40),
  kind: z.literal('path'),
  /** Alternating x, y fractions. */
  points: z.array(fraction).max(4000),
  color: color.default('#ffffff'),
  /** Stroke width as a fraction of the canvas width. */
  width: z.number().min(0.002).max(0.1),
});

export const layerSchema = z.discriminatedUnion('kind', [
  textLayerSchema,
  emojiLayerSchema,
  drawLayerSchema,
]);

export const cardEditSchema = z.object({
  source: z.discriminatedUnion('kind', [
    z.object({ kind: z.literal('media'), mediaId: z.string().min(1) }),
    z.object({ kind: z.literal('url'), url: z.string().url().max(2048) }),
  ]),
  canvas: z.enum(CANVASES),
  /** The part of the source shown, as fractions of the source picture. */
  crop: z.object({
    x: fraction,
    y: fraction,
    w: fraction,
    h: fraction,
    zoom: z.number().min(1).max(10),
  }),
  adjust: z.object({ brightness: signed, contrast: signed, saturation: signed, warmth: signed }),
  layers: z.array(layerSchema).max(50),
});

export type CardEdit = z.infer<typeof cardEditSchema>;
export type Layer = z.infer<typeof layerSchema>;
export type TextLayer = z.infer<typeof textLayerSchema>;
export type EmojiLayer = z.infer<typeof emojiLayerSchema>;
export type DrawLayer = z.infer<typeof drawLayerSchema>;

export const UNTOUCHED = { brightness: 0, contrast: 0, saturation: 0, warmth: 0 } as const;

/** A fresh edit: the whole picture, centre-cropped to the canvas, nothing drawn. */
export function newCardEdit(source: CardEdit['source'], canvas: Canvas): CardEdit {
  return {
    source,
    canvas,
    crop: { x: 0, y: 0, w: 1, h: 1, zoom: 1 },
    adjust: { ...UNTOUCHED },
    layers: [],
  };
}

export const instagramUsernameSchema = z
  .string()
  .trim()
  .min(1)
  .max(30)
  .regex(
    /^[a-zA-Z0-9_.]+$/,
    'Use an Instagram username with letters, numbers, dots or underscores',
  );

/** One tag on a card: an Instagram username placed on the picture. */
export const cardTagSchema = z.object({
  username: instagramUsernameSchema,
  x: fraction,
  y: fraction,
});
export type CardTag = z.infer<typeof cardTagSchema>;

export function centerCrop(width: number, height: number, canvas: Canvas): CardEdit['crop'] {
  const size = CANVAS_SIZE[canvas];
  const aspect = size.width / size.height;
  const w = Math.min(1, (height * aspect) / width);
  const h = Math.min(1, width / aspect / height);
  return { x: (1 - w) / 2, y: (1 - h) / 2, w, h, zoom: 1 };
}

export function fractionPoint(
  point: { x: number; y: number },
  size: { width: number; height: number },
) {
  return { x: point.x * size.width, y: point.y * size.height };
}
export function reframeEdit(
  edit: CardEdit,
  canvas: Canvas,
  width: number,
  height: number,
): CardEdit {
  return { ...edit, canvas, crop: centerCrop(width, height, canvas) };
}
export function normalizeLayerTransform(
  node: { x: number; y: number; width: number; size: number; rotation: number },
  size: { width: number; height: number },
  kind: 'text' | 'emoji',
) {
  const clamp = (n: number, min: number, max: number) =>
    Math.max(min, Math.min(max, Number.isFinite(n) ? n : min));
  return {
    x: clamp(node.x / size.width, 0, 1),
    y: clamp(node.y / size.height, 0, 1),
    w: clamp(node.width / size.width, 0, 1),
    size: clamp(node.size / size.width, kind === 'text' ? 0.01 : 0.02, kind === 'text' ? 0.5 : 0.8),
    rotate: (((node.rotation % 360) + 540) % 360) - 180,
  };
}
